/**
 * PDF Password Handler Hook
 * 
 * Manages the flow of detecting password-protected PDFs and collecting passwords.
 * Provides a queue-based system for handling multiple protected PDFs.
 */

import { useState, useCallback } from "react";
import { checkPdfPassword } from "../utils/pdfPasswordDetector";

/**
 * A file with an optional password attached
 */
export interface FileWithPassword {
  file: File;
  password?: string;
}

/**
 * State of the password handler
 */
interface PasswordHandlerState {
  /** Files that have been processed (with or without passwords) */
  processedFiles: FileWithPassword[];
  /** Files waiting for password input */
  pendingFiles: File[];
  /** Current file being prompted for password */
  currentFile: File | null;
  /** Whether we're currently checking files for password protection */
  isChecking: boolean;
  /** Map of filename to password for API submission */
  filePasswords: Record<string, string>;
}

/**
 * Hook return type
 */
interface UsePdfPasswordHandlerReturn {
  /** All files that are ready (processed) */
  files: File[];
  /** Map of filename to password (for S3 keys) */
  filePasswords: Record<string, string>;
  /** Whether we're currently checking for password protection */
  isChecking: boolean;
  /** Current file needing password (null if none) */
  currentFileNeedingPassword: File | null;
  /** Handle new files dropped/selected */
  handleFilesAdded: (newFiles: File[]) => Promise<void>;
  /** Remove a file from the list */
  removeFile: (file: File) => void;
  /** Called when user submits a password for current file */
  handlePasswordSubmit: (password: string) => void;
  /** Called when user skips the current file */
  handlePasswordSkip: () => void;
  /** Called when user cancels all pending password prompts */
  handlePasswordCancel: () => void;
  /** Clear all files */
  clearFiles: () => void;
  /** Check if modal should be shown */
  showPasswordModal: boolean;
}

/**
 * Hook to manage password-protected PDF detection and password collection
 */
export function usePdfPasswordHandler(): UsePdfPasswordHandlerReturn {
  const [state, setState] = useState<PasswordHandlerState>({
    processedFiles: [],
    pendingFiles: [],
    currentFile: null,
    isChecking: false,
    filePasswords: {},
  });

  /**
   * Process the next file in the pending queue
   */
  const processNextPending = useCallback(() => {
    setState((prev) => {
      if (prev.pendingFiles.length === 0) {
        return { ...prev, currentFile: null };
      }

      const [nextFile, ...remainingFiles] = prev.pendingFiles;
      return {
        ...prev,
        currentFile: nextFile,
        pendingFiles: remainingFiles,
      };
    });
  }, []);

  /**
   * Handle new files being added (dropped or selected)
   */
  const handleFilesAdded = useCallback(async (newFiles: File[]) => {
    if (newFiles.length === 0) return;

    // Filter out duplicates
    setState((prev) => {
      const existingNames = new Set(prev.processedFiles.map((f) => f.file.name));
      const uniqueNewFiles = newFiles.filter((f) => !existingNames.has(f.name));
      
      if (uniqueNewFiles.length === 0) return prev;

      return { ...prev, isChecking: true };
    });

    // Check each file for password protection
    const filesToCheck = newFiles.filter((f) => {
      const name = f.name.toLowerCase();
      return name.endsWith(".pdf");
    });

    const nonPdfFiles = newFiles.filter((f) => {
      const name = f.name.toLowerCase();
      return !name.endsWith(".pdf");
    });

    // Add non-PDF files immediately
    if (nonPdfFiles.length > 0) {
      setState((prev) => ({
        ...prev,
        processedFiles: [
          ...prev.processedFiles,
          ...nonPdfFiles
            .filter((f) => !prev.processedFiles.some((pf) => pf.file.name === f.name))
            .map((file) => ({ file })),
        ],
      }));
    }

    // Check PDFs for password protection
    const passwordProtectedFiles: File[] = [];
    const unprotectedFiles: File[] = [];

    for (const file of filesToCheck) {
      const result = await checkPdfPassword(file);
      if (result.needsPassword) {
        passwordProtectedFiles.push(file);
      } else {
        unprotectedFiles.push(file);
      }
    }

    // Add unprotected PDFs immediately
    setState((prev) => {
      const newProcessedFiles = [
        ...prev.processedFiles,
        ...unprotectedFiles
          .filter((f) => !prev.processedFiles.some((pf) => pf.file.name === f.name))
          .map((file) => ({ file })),
      ];

      // Queue password-protected files
      if (passwordProtectedFiles.length > 0) {
        const [firstProtected, ...restProtected] = passwordProtectedFiles;
        return {
          ...prev,
          processedFiles: newProcessedFiles,
          currentFile: firstProtected,
          pendingFiles: [...prev.pendingFiles, ...restProtected],
          isChecking: false,
        };
      }

      return {
        ...prev,
        processedFiles: newProcessedFiles,
        isChecking: false,
      };
    });
  }, []);

  /**
   * Remove a file from the processed list
   */
  const removeFile = useCallback((fileToRemove: File) => {
    setState((prev) => ({
      ...prev,
      processedFiles: prev.processedFiles.filter(
        (f) => f.file.name !== fileToRemove.name
      ),
      filePasswords: (() => {
        const newPasswords = { ...prev.filePasswords };
        delete newPasswords[fileToRemove.name];
        return newPasswords;
      })(),
    }));
  }, []);

  /**
   * Handle password submission for current file
   */
  const handlePasswordSubmit = useCallback(
    (password: string) => {
      setState((prev) => {
        if (!prev.currentFile) return prev;

        const newFilePasswords = {
          ...prev.filePasswords,
          [prev.currentFile.name]: password,
        };

        const newProcessedFiles = [
          ...prev.processedFiles,
          { file: prev.currentFile, password },
        ];

        // Move to next pending file
        if (prev.pendingFiles.length > 0) {
          const [nextFile, ...remainingFiles] = prev.pendingFiles;
          return {
            ...prev,
            processedFiles: newProcessedFiles,
            filePasswords: newFilePasswords,
            currentFile: nextFile,
            pendingFiles: remainingFiles,
          };
        }

        return {
          ...prev,
          processedFiles: newProcessedFiles,
          filePasswords: newFilePasswords,
          currentFile: null,
        };
      });
    },
    []
  );

  /**
   * Skip the current file (don't add it)
   */
  const handlePasswordSkip = useCallback(() => {
    setState((prev) => {
      // Move to next pending file
      if (prev.pendingFiles.length > 0) {
        const [nextFile, ...remainingFiles] = prev.pendingFiles;
        return {
          ...prev,
          currentFile: nextFile,
          pendingFiles: remainingFiles,
        };
      }

      return {
        ...prev,
        currentFile: null,
      };
    });
  }, []);

  /**
   * Cancel all pending password prompts
   */
  const handlePasswordCancel = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentFile: null,
      pendingFiles: [],
    }));
  }, []);

  /**
   * Clear all files
   */
  const clearFiles = useCallback(() => {
    setState({
      processedFiles: [],
      pendingFiles: [],
      currentFile: null,
      isChecking: false,
      filePasswords: {},
    });
  }, []);

  return {
    files: state.processedFiles.map((f) => f.file),
    filePasswords: state.filePasswords,
    isChecking: state.isChecking,
    currentFileNeedingPassword: state.currentFile,
    handleFilesAdded,
    removeFile,
    handlePasswordSubmit,
    handlePasswordSkip,
    handlePasswordCancel,
    clearFiles,
    showPasswordModal: state.currentFile !== null,
  };
}
